import { useState, useEffect } from "react"
import { Plus, Mail, Phone, Globe, Linkedin, Users, Clock, Star } from "lucide-react"
import { SchoolLayout } from "../components/ui/school/SchoolLayout"
import api from "../hooks/api/apiClient"
import config from "../../config"

const proto = config.appMode === "prototype"

const MOCK_MENTORS = [
  { _id: "1", firstName: "Dr.", lastName: "Chinwe Obi", title: "Software Engineer at Google", bio: "Helping students break into tech.", expertise: ["Software Engineering", "AI", "Data Science"], availability: { type: "online" }, isActive: true },
  { _id: "2", firstName: "Mr.", lastName: "Emeka Nwosu", title: "Investment Banker at Stanbic", bio: "Finance and career guidance.", expertise: ["Finance", "Business", "Accounting"], availability: { type: "in_person" }, isActive: true },
]

const MOCK_SESSIONS = []

function MentorCard({ mentor, onRequestSession, isAdmin, onRemove }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 flex flex-col gap-3">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center text-teal-700 font-bold text-lg">
            {mentor.firstName[0]}{mentor.lastName[0]}
          </div>
          <div>
            <p className="font-bold text-gray-900">{mentor.firstName} {mentor.lastName}</p>
            <p className="text-sm text-gray-500">{mentor.title}</p>
          </div>
        </div>
        {isAdmin && (
          <button onClick={() => onRemove(mentor._id)} className="text-xs text-red-400 hover:text-red-600 font-medium">Remove</button>
        )}
      </div>

      {mentor.bio && <p className="text-sm text-gray-600">{mentor.bio}</p>}

      <div className="flex flex-wrap gap-1.5">
        {mentor.expertise?.map((e) => (
          <span key={e} className="text-xs bg-teal-50 text-teal-700 px-2 py-0.5 rounded-full font-medium">{e}</span>
        ))}
      </div>

      <div className="flex items-center gap-2 text-xs text-gray-500">
        <Clock className="w-3.5 h-3.5" />
        <span className="capitalize">{mentor.availability?.type?.replace("_", " ") ?? "online"}</span>
      </div>

      <div className="flex gap-2 mt-1">
        {mentor.linkedInUrl && <a href={mentor.linkedInUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:text-blue-700"><Linkedin className="w-4 h-4" /></a>}
        {mentor.websiteUrl && <a href={mentor.websiteUrl} target="_blank" rel="noreferrer" className="text-gray-500 hover:text-gray-700"><Globe className="w-4 h-4" /></a>}
        {mentor.email && <a href={`mailto:${mentor.email}`} className="text-gray-500 hover:text-gray-700"><Mail className="w-4 h-4" /></a>}
        {mentor.phone && <a href={`tel:${mentor.phone}`} className="text-gray-500 hover:text-gray-700"><Phone className="w-4 h-4" /></a>}
      </div>

      {!isAdmin && (
        <button
          onClick={() => onRequestSession(mentor)}
          className="mt-1 w-full py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold rounded-lg transition"
        >
          Request Session
        </button>
      )}
    </div>
  )
}

function AddMentorModal({ onClose, onAdded }) {
  const [form, setForm] = useState({ firstName: "", lastName: "", title: "", bio: "", expertise: "", email: "", phone: "", linkedInUrl: "", availability: { type: "online" } })
  const [saving, setSaving] = useState(false)

  const handleSave = async () => {
    if (!form.firstName || !form.title) return
    setSaving(true)
    const payload = { ...form, expertise: form.expertise.split(",").map((s) => s.trim()).filter(Boolean) }
    if (!proto) await api.post("/mentors", payload).catch(() => {})
    onAdded({ _id: Date.now().toString(), ...payload, isActive: true })
    onClose()
    setSaving(false)
  }

  const field = (label, key, type = "text", placeholder = "") => (
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-1">{label}</label>
      <input
        type={type}
        value={key === "type" ? form.availability.type : form[key] ?? ""}
        onChange={(e) => key === "type"
          ? setForm((f) => ({ ...f, availability: { ...f.availability, type: e.target.value } }))
          : setForm((f) => ({ ...f, [key]: e.target.value }))}
        placeholder={placeholder}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
      />
    </div>
  )

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">Add Mentor</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold">×</button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {field("First Name *", "firstName")}
            {field("Last Name *", "lastName")}
          </div>
          {field("Job Title / Role *", "title", "text", "e.g. Software Engineer at Google")}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Bio</label>
            <textarea value={form.bio} onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))} rows={3} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none" placeholder="Brief bio for students" />
          </div>
          {field("Expertise (comma-separated)", "expertise", "text", "e.g. Software Engineering, AI, Data Science")}
          {field("Email", "email", "email")}
          {field("Phone", "phone", "tel")}
          {field("LinkedIn URL", "linkedInUrl", "url")}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Session Type</label>
            <select value={form.availability.type} onChange={(e) => setForm((f) => ({ ...f, availability: { ...f.availability, type: e.target.value } }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500">
              <option value="online">Online</option>
              <option value="in_person">In Person</option>
              <option value="both">Both</option>
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
          <button onClick={handleSave} disabled={saving || !form.firstName || !form.title} className="px-5 py-2 text-sm font-bold text-white bg-teal-600 hover:bg-teal-700 disabled:opacity-50 rounded-lg">
            {saving ? "Saving…" : "Add Mentor"}
          </button>
        </div>
      </div>
    </div>
  )
}

export default function MentorManagementPage() {
  const [mentors, setMentors] = useState([])
  const [sessions, setSessions] = useState([])
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState("mentors")
  const [showAdd, setShowAdd] = useState(false)

  const load = () => {
    setLoading(true)
    if (proto) {
      setMentors(MOCK_MENTORS)
      setSessions(MOCK_SESSIONS)
      setLoading(false)
      return
    }
    Promise.all([
      api.get("/mentors").then((r) => r.data.data.mentors),
      api.get("/mentors/sessions").then((r) => r.data.data.sessions),
    ])
      .then(([m, s]) => { setMentors(m); setSessions(s) })
      .catch(() => {})
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [])

  const handleRemove = async (id) => {
    if (!proto) await api.delete(`/mentors/${id}`).catch(() => {})
    setMentors((prev) => prev.filter((m) => m._id !== id))
  }

  const handleSessionAction = async (id, status) => {
    if (!proto) await api.patch(`/mentors/sessions/${id}`, { status }).catch(() => {})
    setSessions((prev) => prev.map((s) => s._id === id ? { ...s, status } : s))
  }

  const pendingCount = sessions.filter((s) => s.status === "pending").length

  return (
    <SchoolLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-1">Career Mentors</h1>
            <p className="text-gray-500">Connect students with industry professionals.</p>
          </div>
          <button onClick={() => setShowAdd(true)} className="flex items-center gap-2 px-5 py-2.5 bg-teal-600 hover:bg-teal-700 text-white text-sm font-bold rounded-lg transition">
            <Plus className="w-4 h-4" /> Add Mentor
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center"><Users className="w-5 h-5 text-teal-600" /></div>
            <div><p className="text-2xl font-bold text-gray-900">{mentors.length}</p><p className="text-sm text-gray-500">Total Mentors</p></div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center"><Clock className="w-5 h-5 text-amber-600" /></div>
            <div><p className="text-2xl font-bold text-gray-900">{pendingCount}</p><p className="text-sm text-gray-500">Pending Requests</p></div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center"><Star className="w-5 h-5 text-green-600" /></div>
            <div><p className="text-2xl font-bold text-gray-900">{sessions.filter((s) => s.status === "completed").length}</p><p className="text-sm text-gray-500">Completed Sessions</p></div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-gray-100 rounded-lg p-1 w-fit">
          {["mentors", "sessions"].map((t) => (
            <button key={t} onClick={() => setTab(t)} className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-all ${tab === t ? "bg-white text-teal-700 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
              {t === "mentors" ? "Mentors" : `Session Requests ${pendingCount > 0 ? `(${pendingCount})` : ""}`}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-16 text-gray-400">Loading…</div>
        ) : tab === "mentors" ? (
          mentors.length === 0 ? (
            <div className="text-center py-16 text-gray-400">No mentors yet. Add your first mentor to get started.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {mentors.map((m) => (
                <MentorCard key={m._id} mentor={m} isAdmin={true} onRemove={handleRemove} onRequestSession={() => {}} />
              ))}
            </div>
          )
        ) : (
          sessions.length === 0 ? (
            <div className="text-center py-16 text-gray-400">No session requests yet.</div>
          ) : (
            <div className="space-y-3">
              {sessions.map((s) => (
                <div key={s._id} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center justify-between gap-4">
                  <div>
                    <p className="font-semibold text-gray-900">{s.studentId?.firstName} {s.studentId?.lastName} <span className="text-gray-400 font-normal">→</span> {s.mentorId?.firstName} {s.mentorId?.lastName}</p>
                    <p className="text-sm text-gray-500 mt-0.5">{s.note ?? "No note"}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${s.status === "pending" ? "bg-amber-100 text-amber-700" : s.status === "confirmed" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>{s.status}</span>
                    {s.status === "pending" && (
                      <>
                        <button onClick={() => handleSessionAction(s._id, "confirmed")} className="text-xs font-semibold text-green-600 hover:text-green-700 border border-green-200 px-2 py-1 rounded">Confirm</button>
                        <button onClick={() => handleSessionAction(s._id, "cancelled")} className="text-xs font-semibold text-red-500 hover:text-red-600 border border-red-200 px-2 py-1 rounded">Cancel</button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>

      {showAdd && <AddMentorModal onClose={() => setShowAdd(false)} onAdded={(m) => setMentors((prev) => [m, ...prev])} />}
    </SchoolLayout>
  )
}
